import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useBooking } from "@/lib/bookingStore";

const EASE = [0.16, 1, 0.3, 1];

const PRINCIPLES = ["Senior only", "Zero handoffs", "Here after launch"];

export const About = () => {
    const openBooking = useBooking((s) => s.openBooking);
    return (
    <section
        id="about"
        data-testid="about-section"
        className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32"
    >
        <div className="grid gap-16 md:grid-cols-12">
            <div className="md:col-span-4">
                <div className="md:sticky md:top-32">
                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: EASE }}
                        className="font-mono-x text-xs uppercase tracking-[0.25em] text-zinc-500"
                    >
                        [ About RaRe ]
                    </motion.p>
                </div>
            </div>

            <div className="md:col-span-8">
                <motion.h2
                    data-testid="about-headline"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 1, ease: EASE }}
                    className="font-display max-w-4xl text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl"
                >
                    Execution is the{" "}
                    <span className="font-logo font-extrabold">rarest</span> thing in
                    business.
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
                    className="mt-12 max-w-3xl text-base leading-relaxed text-zinc-400 md:text-xl"
                >
                    We spent over twelve years combined inside product companies and growth teams —
                    shipping software at MNC scale and running marketing that had to
                    justify every rupee. RaRe is where that discipline goes to work
                    for founders.
                </motion.p>

                <motion.p
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
                    className="mt-8 max-w-3xl text-base leading-relaxed text-zinc-400 md:text-xl"
                >
                    No account managers, no handoffs, no agencies marking up
                    freelancers. The people you meet on the first call are the people
                    who design, build and market your product — and stay after
                    launch.
                </motion.p>

                <motion.div
                    data-testid="about-principles"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
                    className="mt-14 flex flex-wrap gap-3"
                >
                    {PRINCIPLES.map((principle) => (
                        <span
                            key={principle}
                            className="rounded-full border border-white/10 px-4 py-2 font-mono-x text-[11px] uppercase tracking-[0.2em] text-zinc-500 transition-colors duration-300 hover:border-white/30 hover:text-white"
                        >
                            {principle}
                        </span>
                    ))}
                </motion.div>

                <motion.button
                    data-testid="about-book-call-link"
                    onClick={openBooking}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.4 }}
                    className="group mt-12 inline-flex items-center gap-2 font-mono-x text-xs uppercase tracking-[0.2em] text-white"
                >
                    <span className="border-b border-white/30 pb-1 transition-colors duration-300 group-hover:border-white">
                        Meet us on a 30-min call
                    </span>
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                </motion.button>
            </div>
        </div>
    </section>
    );
};
